import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { string, object } from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import { storeFunction, updateFunction } from "../../../../../services/pwsApi";

const functionSchema = object().shape({
  description: string().required("Informe o nome da função."),
  cbo: string()
    .required("Informe o CBO da função.")
    .test("is-cbo", "O valor não corresponde a um CBO válido.", value => {
      return value?.length === 6;
    }),
});

export function FunctionsDialog({ onClose, data }) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const companyCode = searchParams.get("company_code") || "";
  const company = companyCode.slice(0, 3);
  const subsidiary = companyCode.slice(3, 7);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(functionSchema),
    defaultValues: {
      functionId: data?.functionId ?? null,
      code: data?.code ?? null,
      description: data?.description ?? "",
      cbo: data?.cbo ?? "",
    },
  });

  const storeMutation = useMutation(storeFunction, {
    onSuccess: () => {
      toast.success("Função criada com sucesso.");
      queryClient.invalidateQueries("functions");
      onClose();
    },
    onError: () => {
      toast.error("Erro ao criar função.");
    },
  });

  const updateMutation = useMutation(
    variables => updateFunction(variables.functionId, variables),
    {
      onSuccess: () => {
        toast.success("Função atualizada com sucesso.");
        queryClient.invalidateQueries("functions");
        onClose();
      },
      onError: () => {
        toast.error("Erro ao atualizar função.");
      },
    },
  );

  const onSubmit = formData => {
    const { functionId, description, cbo } = formData;

    const payload = {
      companyCode: company,
      subsidiaryCode: subsidiary,
      description,
      cbo,
    };

    if (functionId) {
      updateMutation.mutate({ functionId, ...payload });
    } else {
      storeMutation.mutate(payload);
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth open onClose={onClose}>
      <DialogTitle>
        {!data?.functionId ? "Adicionar Função" : "Editar Função"}
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          <div className="col-sm-12">
            <TextField
              label="Código"
              margin="normal"
              fullWidth
              variant="standard"
              value={data?.code ?? ""}
              disabled
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome da função"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.functionName}
                  helperText={errors.functionName?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <Controller
              name="cbo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="CBO da função"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.cbo}
                  helperText={errors.cbo?.message}
                />
              )}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          {!data?.functionId ? "Adicionar" : "Editar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
